// Turns an invoice into a render-ready model shared by the on-screen preview,
// the PDF and the Excel export so all three always match.
//
// `data` is the store (or any object with { settings, brands, clients }).

import { getTemplate } from "./templates";
import { COPY_LABELS, DOC_TYPES, amountInWords, computeTotals, fmtDate, fmtMoney, num } from "./utils";

const EU_VAT = /^(united kingdom|uk|great britain|england|scotland|wales|northern ireland|ireland|germany|france|spain|italy|netherlands|belgium|austria|portugal|sweden|denmark|finland|poland|czech|greece|hungary|romania|bulgaria|croatia|slovakia|slovenia|lithuania|latvia|estonia|luxembourg|malta|cyprus)/i;
// "GSTIN" for Indian numbers, "VAT No" in the UK/EU, otherwise "Tax ID".
export function taxIdLabel(id, country = "") {
  if (/^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z0-9]{3}$/i.test(String(id).replace(/\s/g, "")) || /^india$/i.test(country || "")) return "GSTIN";
  if (EU_VAT.test(country || "") || /^(GB|XI|EU|DE|FR|IE|NL|ES|IT|BE)\s?\d/i.test(String(id))) return "VAT No";
  if (/^(australia|new zealand|singapore|canada)$/i.test(country || "")) return /^canada$/i.test(country) ? "GST/HST No" : "GST No";
  return "Tax ID";
}

export function resolveBrand(inv, data) {
  const brands = data.brands || [];
  return (
    brands.find((b) => b.id === inv.brandId) ||
    brands.find((b) => b.id === data.settings?.defaultBrandId) ||
    brands[0] ||
    {}
  );
}

export function resolveTemplateKey(inv, data) {
  const client = (data.clients || []).find((c) => c.id === inv.clientId);
  return inv.template || client?.template || resolveBrand(inv, data).template || "classic";
}

export function buildDocument(inv, data, { pdf = false } = {}) {
  const settings = data.settings || {};
  const t = computeTotals(inv);
  const cur = inv.currency || "GBP";
  const m = (n) => fmtMoney(n, cur, { pdf });
  const co = resolveBrand(inv, data);
  const client = (data.clients || []).find((c) => c.id === inv.clientId);
  const d = settings.defaults || {};
  const b = inv.billTo || {};
  const dateStyle = d.dateStyle || "iso";
  const type = inv.docType || "invoice";
  const isCredit = type === "credit";
  const templateKey = resolveTemplateKey(inv, data);

  const half = t.rate != null ? t.rate / 2 : null;
  const pct = (r) => (r == null ? "" : ` @${+r.toFixed(2)}%`);

  const summary = [{ label: "AMOUNT", value: m(t.subtotal) }];
  if (inv.taxMode === "intra") {
    summary.push({ label: "CGST" + pct(half), value: m(t.cgst) });
    summary.push({ label: "SGST" + pct(half), value: m(t.sgst) });
  } else if (inv.taxMode === "inter") {
    summary.push({ label: "IGST" + pct(t.rate), value: m(t.igst) });
  } else if (inv.taxMode === "vat") {
    summary.push({ label: (inv.taxLabel || "VAT") + pct(t.rate), value: m(t.vat) });
  } else if (inv.taxMode === "export") {
    summary.push({ label: "IGST @0% (Export)", value: m(0) });
  }
  if (t.discount) summary.push({ label: "Discount", value: "-" + m(t.discount), tone: "muted" });
  if (t.credit) summary.push({ label: inv.creditLabel || "Credit Amount", value: m(t.credit), tone: "credit" });
  if (t.roundOff) summary.push({ label: "Round off", value: (t.roundOff < 0 ? "-" : "") + m(Math.abs(t.roundOff)), tone: "muted" });
  summary.push({ label: isCredit ? "CREDIT TOTAL" : "TOTAL AMOUNT", value: m(t.total), tone: "total" });
  if (t.paid && !isCredit) {
    if (t.received) summary.push({ label: "Paid", value: m(t.received), tone: "muted" });
    if (t.credited) summary.push({ label: "Credit notes", value: m(t.credited), tone: "muted" });
    if (t.tds) summary.push({ label: "TDS deducted", value: m(t.tds), tone: "muted" });
    summary.push({ label: "BALANCE DUE", value: m(t.balance), tone: "total" });
  }
  const fx = num(inv.fxRate);
  if (cur !== "INR" && fx > 0) {
    summary.push({ label: `INR value @ ${fx}`, value: fmtMoney(t.total * fx, "INR", { pdf }), tone: "muted" });
  }

  const billLines = [
    b.contactPerson && `Attn: ${b.contactPerson}`,
    [b.address, b.area].filter(Boolean).join(", "),
    [b.city, b.state && b.state !== b.country && b.state !== b.city ? b.state : "", b.postcode].filter(Boolean).join(", "),
    b.country,
    b.phone && `Mobile: ${b.phone}`,
    b.email && `Email: ${b.email}`,
    b.gstin && `${taxIdLabel(b.gstin, b.country)}: ${b.gstin}`,
  ].filter(Boolean);

  const noun = type === "quote" ? "Quote" : isCredit ? "Credit Note" : "Invoice";
  const meta = [
    { label: `${noun} No:`, value: inv.number || "—" },
    { label: `${noun} Date:`, value: fmtDate(inv.date, dateStyle) },
  ];
  if (!isCredit) meta.push({ label: type === "quote" ? "Valid Until:" : "Due Date:", value: fmtDate(inv.dueDate, dateStyle) });

  const extraMeta = [
    isCredit && inv.againstNumber && { label: "Against Invoice", value: inv.againstNumber },
    inv.period && { label: "Billing Period", value: inv.period },
    inv.placeOfSupply && { label: "Place of Supply", value: inv.placeOfSupply },
    inv.reference && { label: "Reference / PO", value: inv.reference },
  ].filter(Boolean);

  const useInr = cur === "INR" && co.bankInr?.accountNumber;
  const bk = (useInr ? co.bankInr : co.bank) || {};
  const bank = isCredit
    ? []
    : (useInr
        ? [
            ["Account holder name", bk.accountName],
            ["Account number", bk.accountNumber],
            ["IFSC", bk.ifsc],
            ["Bank name", bk.bankName],
            ["Branch", bk.branch],
            ["UPI", bk.upiId],
          ]
        : [
            ["Account holder name", bk.accountName],
            ["Payment method", bk.method],
            ["Account currency", bk.currency],
            ["Account number", bk.accountNumber],
            ["Sort code", bk.sortCode],
            ["IBAN", bk.iban],
            ["SWIFT / BIC", bk.swift],
            ["IFSC", bk.ifsc],
            ["Bank name", bk.bankName],
            ["Beneficiary address", bk.bankAddress],
          ]
      ).filter(([, v]) => v);

  let qr = "";
  if (d.showQr && inv.showQr !== false && !isCredit && t.balance > 0) {
    const upi = co.bankInr?.upiId;
    if (cur === "INR" && upi) {
      qr = `upi://pay?pa=${encodeURIComponent(upi)}&pn=${encodeURIComponent(co.name || "")}&am=${t.balance.toFixed(2)}&cu=INR&tn=${encodeURIComponent(inv.number || "")}`;
    } else if (d.qrText) {
      qr = d.qrText.replaceAll("{amount}", t.balance.toFixed(2)).replaceAll("{number}", inv.number || "").replaceAll("{currency}", cur);
    }
  }

  const taxHeader = t.rate != null ? `TAX (${+t.rate.toFixed(2)}%)` : "TAX";

  const footNotes = [];
  if (inv.taxMode === "export") {
    footNotes.push(`Supply meant for export of services under LUT${co.lut ? ` (${co.lut})` : ""} without payment of IGST.`);
  }
  if (inv.taxMode === "none" && co.gstin === "" && type === "invoice") footNotes.push("Not registered under GST.");

  const showClientLogo = inv.showClientLogo ?? d.showClientLogo;

  return {
    template: getTemplate(templateKey),
    templateKey,
    accent: co.accent || "#C62828",
    title: inv.title || DOC_TYPES[type].title,
    copyLabel: inv.copyLabel ?? COPY_LABELS[0],
    company: {
      name: co.name,
      tagline: co.tagline,
      logo: co.logo,
      signature: co.signature,
      address: co.address,
      lines: [
        [co.address, co.area, co.city, co.postcode].filter(Boolean).join(", "),
        co.country && !/^india$/i.test(co.country) ? co.country : "",
        co.gstin && `${taxIdLabel(co.gstin, co.country)}: ${co.gstin}`,
        co.pan && `PAN: ${co.pan}`,
        [co.email && `Email: ${co.email}`, co.phone && `Phone: ${co.phone}`].filter(Boolean).join("  |  "),
        co.website,
      ].filter(Boolean),
      signatory: co.signatory || "Authorised Signatory",
    },
    meta,
    extraMeta,
    billTo: { name: b.name || "—", lines: billLines, logo: showClientLogo ? client?.logo || "" : "" },
    showHsn: !!inv.showHsn,
    taxHeader,
    rows: t.lines.map((l, i) => ({
      sr: i + 1,
      description: l.description || "",
      hsn: l.hsn || "",
      qty: +l.qty.toFixed(2),
      rate: m(l.rate),
      tax: m(l.tax),
      amount: m(l.amount),
    })),
    subtotal: m(t.subtotal),
    summary,
    words: amountInWords(t.total, cur),
    bank,
    qr,
    notes: inv.notes || "",
    terms: inv.terms || "",
    footNotes,
    totals: t,
  };
}
